import React, { useRef } from 'react';
import { Box, alpha } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import ProviderCard from '../Provider/ProviderCard';
import { categoryLandingPath } from '../../utils/landingPaths';

// Swiper styles importálása
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

/**
 * Featured Carousel - Swiper alapú
 * @param {array} providers - Kiemelt szolgáltatók tömbje
 * @param {string} listName - A lista neve az analitikához
 */
const FeaturedCarousel = ({ providers, listName = 'home_featured' }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  return (
    <Box
      sx={{
        position: 'relative',
        px: { xs: 1, sm: 2, md: 7 },
        // A Swiper CSS a .swiper-wrapper-t content-box-ra állítja, amit a MUI CssBaseline (box-sizing: inherit)
        // a kártyákba is továbbörökít — ettől a karusszelben nagyobb lett az avatar és az ellenőrzött-pipa.
        '& .swiper-slide': { boxSizing: 'border-box' },
      }}
    >
      {/* Léptető nyilak: a Swiper a ref-eken keresztül köti be őket */}
      {/* A ref-eknek a Swiper inicializálása ELŐTT kell léteznie, ezért mindkét nyíl a Swiper előtt van */}
      {/* Saját ikon: a Swiper alapértelmezett nyílja a gombon kívülre lógott */}
      <Box ref={prevRef} className="swiper-button-prev">
        <ChevronLeft />
      </Box>
      <Box ref={nextRef} className="swiper-button-next">
        <ChevronRight />
      </Box>
      <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        spaceBetween={24}
        slidesPerView={1}
        navigation={{
          prevEl: prevRef.current,
          nextEl: nextRef.current,
        }}
        onBeforeInit={(swiper) => {
          swiper.params.navigation.prevEl = prevRef.current;
          swiper.params.navigation.nextEl = nextRef.current;
        }}
        pagination={{
          clickable: true,
          dynamicBullets: true,
        }}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        loop={providers.length > 3}
        breakpoints={{
          // Reszponzív beállítások
          600: {
            slidesPerView: 2,
            spaceBetween: 20,
          },
          960: {
            slidesPerView: 3,
            spaceBetween: 24,
          },
        }}
        style={{
          paddingBottom: '50px', // Hely a pagination-nek
        }}
      >
        {providers.map((provider, index) => (
          <SwiperSlide key={provider.id} style={{ height: 'auto' }}>
            <ProviderCard
              provider={provider}
              animationDelay={index * 100}
              listPosition={index + 1}
              listName={listName}
              categoryHref={categoryLandingPath}
              onCardClick={(provider) => {
                navigate(`/szolgaltato/${provider.slug || provider.id}`);
              }}
            />
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Custom navigation styles */}
      <style jsx global>{`
        .swiper-button-next,
        .swiper-button-prev {
          background: linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%);
          box-shadow: 0 4px 20px ${alpha(theme.palette.primary.main, 0.4)};
          width: 44px;
          height: 44px;
          border-radius: 50%;
          color: #fff !important;
          transition: all 0.3s ease;
        }

        .swiper-button-next svg,
        .swiper-button-prev svg {
          width: 26px;
          height: 26px;
        }

        .swiper-button-next:hover,
        .swiper-button-prev:hover {
          background: linear-gradient(45deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%);
          box-shadow: 0 8px 30px ${alpha(theme.palette.primary.main, 0.6)};
          transform: translateY(-2px);
        }

        .swiper-pagination-bullet {
          background-color: ${theme.palette.grey[400]};
          opacity: 0.5;
          width: 12px;
          height: 12px;
        }

        .swiper-pagination-bullet-active {
          background-color: ${theme.palette.secondary.main};
          opacity: 1;
        }

        /* Reszponzív navigation elrejtés kis képernyőn */
        @media (max-width: 600px) {
          .swiper-button-next,
          .swiper-button-prev {
            display: none;
          }
        }
      `}</style>
    </Box>
  );
};

export default FeaturedCarousel;